import * as React from 'react'
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

type ChannelCompletionData = {
  message: string
  completedAt: number
}

type BackfillContextType = {
  markChannelCompleted: (channelId: string, message: string) => void
  hasChannelCompleted: (channelId: string) => boolean
  getChannelCompletionMessage: (channelId: string) => string | undefined
  getChannelCompletionTimestamp: (channelId: string) => number | undefined
  clearChannelCompleted: (channelId: string) => void
  markChannelInitiated: (channelId: string) => void
  clearChannelInitiated: (channelId: string) => void
  hasChannelInitiated: (channelId: string) => boolean
  markChannelFirstSync: (channelId: string) => void
  hasChannelFirstSync: (channelId: string) => boolean
}

const BackfillContext = createContext<BackfillContextType | null>(null)

export function BackfillProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const [completedChannels, setCompletedChannels] = useState<
    Map<string, ChannelCompletionData>
  >(new Map())
  const [initiatedChannels, setInitiatedChannels] = useState<Set<string>>(
    new Set(),
  )
  // Channels whose current backfill is their first ever sync (no prior
  // `lastCheckedAt`) — i.e. the just-connected case. Captured at trigger time
  // and read once backfill completes.
  const [firstSyncChannels, setFirstSyncChannels] = useState<Set<string>>(
    new Set(),
  )

  // Use refs to store the latest state for getter functions
  // CRITICAL: Update refs synchronously on every render (not in useEffect)
  // to ensure getters IMMEDIATELY read fresh data after state changes
  const completedChannelsRef = React.useRef(completedChannels)
  const initiatedChannelsRef = React.useRef(initiatedChannels)
  const firstSyncChannelsRef = React.useRef(firstSyncChannels)
  completedChannelsRef.current = completedChannels
  initiatedChannelsRef.current = initiatedChannels
  firstSyncChannelsRef.current = firstSyncChannels

  const markChannelCompleted = useCallback(
    (channelId: string, message: string): void => {
      setCompletedChannels((prev) => {
        const next = new Map(prev)
        next.set(channelId, {
          message,
          completedAt: Date.now(),
        })
        return next
      })
      // Clear initiated state when channel completes
      setInitiatedChannels((prev) => {
        const next = new Set(prev)
        next.delete(channelId)
        return next
      })
    },
    [],
  )

  const hasChannelCompleted = useCallback((channelId: string): boolean => {
    return completedChannelsRef.current.has(channelId)
  }, [])

  const getChannelCompletionMessage = useCallback(
    (channelId: string): string | undefined => {
      return completedChannelsRef.current.get(channelId)?.message
    },
    [],
  )

  const getChannelCompletionTimestamp = useCallback(
    (channelId: string): number | undefined => {
      return completedChannelsRef.current.get(channelId)?.completedAt
    },
    [],
  )

  const clearChannelCompleted = useCallback((channelId: string): void => {
    setCompletedChannels((prev) => {
      const next = new Map(prev)
      next.delete(channelId)
      return next
    })
  }, [])

  const markChannelInitiated = useCallback((channelId: string): void => {
    setInitiatedChannels((prev) => {
      const next = new Set(prev)
      next.add(channelId)
      return next
    })
  }, [])

  const clearChannelInitiated = useCallback((channelId: string): void => {
    setInitiatedChannels((prev) => {
      const next = new Set(prev)
      next.delete(channelId)
      return next
    })
  }, [])

  const hasChannelInitiated = useCallback((channelId: string): boolean => {
    return initiatedChannelsRef.current.has(channelId)
  }, [])

  const markChannelFirstSync = useCallback((channelId: string): void => {
    setFirstSyncChannels((prev) => {
      const next = new Set(prev)
      next.add(channelId)
      return next
    })
  }, [])

  const hasChannelFirstSync = useCallback((channelId: string): boolean => {
    return firstSyncChannelsRef.current.has(channelId)
  }, [])

  const contextValue: BackfillContextType = useMemo(
    () => ({
      markChannelCompleted,
      hasChannelCompleted,
      getChannelCompletionMessage,
      getChannelCompletionTimestamp,
      clearChannelCompleted,
      markChannelInitiated,
      clearChannelInitiated,
      hasChannelInitiated,
      markChannelFirstSync,
      hasChannelFirstSync,
    }),
    [
      markChannelCompleted,
      hasChannelCompleted,
      getChannelCompletionMessage,
      getChannelCompletionTimestamp,
      clearChannelCompleted,
      markChannelInitiated,
      clearChannelInitiated,
      hasChannelInitiated,
      markChannelFirstSync,
      hasChannelFirstSync,
    ],
  )

  return (
    <BackfillContext.Provider value={contextValue}>
      {children}
    </BackfillContext.Provider>
  )
}

export function useBackfillCompletion(): BackfillContextType {
  const context = useContext(BackfillContext)
  if (!context) {
    throw new Error(
      'useBackfillCompletion must be used within BackfillProvider',
    )
  }
  return context
}
